/**
 * ship/glass.ts — Fake-glass ShaderMaterial for canopy + side windows
 *
 * §6.2 spec:
 *   - Fresnel-weighted tint: pow(1 - dot(N,V), 3)
 *   - Faint blue-grey rgba(0.7, 0.85, 1.0, 0.03–0.12)
 *   - Static smudge/dust texture (2K canvas, shared) modulated by fresnel
 *   - Faint specular streak from sun direction
 *   - depthWrite: false, transparent: true
 *   - BackSide only — avoids DoubleSide double-blend and multi-pane stacking
 *
 * No refraction, no transmission, no post-processing.
 */

import * as THREE from 'three';

// ── Smudge texture (2K procedural, generated once) ─────────────
let _smudgeTex: THREE.CanvasTexture | null = null;

function _getSmudgeTexture(): THREE.CanvasTexture {
  if (_smudgeTex) return _smudgeTex;

  const size = 2048;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  // Dark base (smudges only ADD brightness at glancing angles)
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, size, size);

  const rng = mulberry32(12345);
  for (let i = 0; i < 300; i++) {
    const x = rng() * size;
    const y = rng() * size;
    const r = 10 + rng() * 80;
    const alpha = rng() * 0.15;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255,255,255,${alpha})`;
    ctx.fill();
  }

  for (let i = 0; i < 20; i++) {
    const x0 = rng() * size;
    const y0 = rng() * size;
    const len = 100 + rng() * 400;
    const angle = rng() * Math.PI * 2;
    const x1 = x0 + Math.cos(angle) * len;
    const y1 = y0 + Math.sin(angle) * len;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.strokeStyle = `rgba(255,255,255,${rng() * 0.08})`;
    ctx.lineWidth = 2 + rng() * 8;
    ctx.stroke();
  }

  _smudgeTex = new THREE.CanvasTexture(canvas);
  _smudgeTex.wrapS = THREE.RepeatWrapping;
  _smudgeTex.wrapT = THREE.RepeatWrapping;
  _smudgeTex.minFilter = THREE.LinearMipmapLinearFilter;
  _smudgeTex.magFilter = THREE.LinearFilter;
  _smudgeTex.generateMipmaps = true;
  return _smudgeTex;
}

function mulberry32(seed: number) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = (seed ^ (seed >>> 15)) * (1 | seed);
    t = (t + (t ^ (t >>> 7)) * (61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Shader source ──────────────────────────────────────────────

const vertexShader = /* glsl */ `
varying vec3 vWorldNormal;
varying vec3 vWorldPosition;
varying vec2 vUv;

void main() {
  vec4 worldPos = modelMatrix * vec4(position, 1.0);
  vWorldPosition = worldPos.xyz;
  vWorldNormal = normalize(mat3(modelMatrix) * normal);
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const fragmentShader = /* glsl */ `
varying vec3 vWorldNormal;
varying vec3 vWorldPosition;
varying vec2 vUv;

uniform vec3 uSunDir;
uniform sampler2D uSmudgeTex;
uniform bool uDebugTransparent;

void main() {
  // Debug: discard everything (K key passthrough)
  if (uDebugTransparent) { discard; return; }

  // Use built-in cameraPosition (per-eye correct in VR)
  vec3 V = normalize(cameraPosition - vWorldPosition);
  vec3 N = normalize(vWorldNormal);

  // Fresnel: glancing angle = higher
  float fresnel = pow(1.0 - abs(dot(N, V)), 3.0);

  // Tint: faint blue-grey, stronger at glancing angles
  vec3 tintColor = vec3(0.7, 0.85, 1.0);
  float tintStrength = mix(0.03, 0.10, fresnel);
  vec3 baseColor = tintColor * tintStrength;

  // Smudge texture: only visible at glancing angles, ADDS brightness
  float smudge = texture2D(uSmudgeTex, vUv * 4.0).r;
  float smudgeStrength = fresnel * 0.08;
  baseColor += smudge * smudgeStrength;

  // Specular streak from sun direction
  vec3 H = normalize(uSunDir + V);
  float spec = pow(max(dot(N, H), 0.0), 256.0);
  baseColor += vec3(1.0, 0.95, 0.8) * spec * 0.15;

  // Alpha: nearly transparent head-on, slightly more opaque at edges
  // Clamped to safe range so glass never blocks more than ~10% brightness
  float alpha = clamp(0.03 + 0.09 * fresnel, 0.0, 0.15) + spec * 0.05;
  alpha = clamp(alpha, 0.02, 0.18);

  gl_FragColor = vec4(baseColor, alpha);
}
`;

// ── Material factory ───────────────────────────────────────────

let _sharedMaterial: THREE.ShaderMaterial | null = null;

export function createGlassMaterial(): THREE.ShaderMaterial {
  if (_sharedMaterial) return _sharedMaterial;

  _sharedMaterial = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uSunDir: { value: new THREE.Vector3(1, 0, 0) },
      uSmudgeTex: { value: _getSmudgeTexture() },
      uDebugTransparent: { value: false },
    },
    transparent: true,
    depthWrite: false,
    side: THREE.BackSide, // render inner face only — no DoubleSide double-blend
  });

  return _sharedMaterial;
}

let _debugTransparent = false;

export function toggleDebugTransparent(): boolean {
  _debugTransparent = !_debugTransparent;
  if (_sharedMaterial) {
    _sharedMaterial.uniforms.uDebugTransparent.value = _debugTransparent;
  }
  return _debugTransparent;
}

/**
 * Update per-frame uniforms. Only sunDir needed now; V uses built-in cameraPosition.
 */
export function updateGlassUniforms(sunDir: THREE.Vector3): void {
  if (!_sharedMaterial) return;
  _sharedMaterial.uniforms.uSunDir.value.copy(sunDir);
}
